import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { EmployeeDesignation, ReleaseAnnouncementDraft, ReleaseFeature, UserRole } from '../../types';
import { hasReleaseAudienceSelection } from '../../utils/releaseAudience';
import {
  subscribeToPendingReleaseDraft,
  draftToPreviewRelease,
  publishReleaseDraft,
  skipReleaseDraft,
} from '../../utils/releaseAnnouncements';
import ReleaseAnnouncementPopup from './ReleaseAnnouncementPopup';

const SHOW_DELAY_MS = 1500;
const SESSION_KEY_PREFIX = 'lctf:releaseDraftLater:';

/**
 * Prompts the CRM admin to review and publish auto-generated release notes
 * after CI saves a pending draft on deploy.
 */
const ReleasePublishPrompt: React.FC = () => {
  const { user } = useAuth();
  const [draft, setDraft] = useState<ReleaseAnnouncementDraft | null>(null);
  const [visible, setVisible] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audienceRoles, setAudienceRoles] = useState<UserRole[]>([]);
  const [audienceDesignations, setAudienceDesignations] = useState<EmployeeDesignation[]>([]);
  const [audienceUserIds, setAudienceUserIds] = useState<string[]>([]);
  const [features, setFeatures] = useState<ReleaseFeature[]>([]);
  const [title, setTitle] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) return;
    return subscribeToPendingReleaseDraft(setDraft);
  }, [isAdmin]);

  useEffect(() => {
    if (draft) {
      setAudienceRoles(draft.audienceRoles);
      setAudienceDesignations(draft.audienceDesignations ?? []);
      setAudienceUserIds(draft.audienceUserIds ?? []);
      setFeatures(draft.features);
      setTitle(draft.title);
    }
  }, [draft]);

  useEffect(() => {
    if (!isAdmin || !draft) {
      setVisible(false);
      return;
    }
    try {
      if (sessionStorage.getItem(`${SESSION_KEY_PREFIX}${draft.id}`)) return;
    } catch {
      /* ignore */
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isAdmin, draft]);

  const handleLater = useCallback(() => {
    if (draft) {
      try {
        sessionStorage.setItem(`${SESSION_KEY_PREFIX}${draft.id}`, '1');
      } catch {
        /* ignore */
      }
    }
    setVisible(false);
    setError(null);
  }, [draft]);

  const handleSkip = useCallback(async () => {
    if (!draft) return;
    setPublishing(true);
    setError(null);
    try {
      await skipReleaseDraft(draft.id);
      setVisible(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not skip draft.');
    } finally {
      setPublishing(false);
    }
  }, [draft]);

  const handlePublish = useCallback(async () => {
    if (!draft) return;
    if (!hasReleaseAudienceSelection(audienceRoles, audienceDesignations, audienceUserIds)) return;
    setPublishing(true);
    setError(null);
    try {
      await publishReleaseDraft(draft, { title, audienceRoles, audienceDesignations, audienceUserIds, features });
      setVisible(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish release.');
    } finally {
      setPublishing(false);
    }
  }, [draft, title, audienceRoles, audienceDesignations, audienceUserIds, features]);

  if (!isAdmin || !visible || !draft) return null;

  const previewRelease = {
    ...draftToPreviewRelease(draft),
    title: title || draft.title,
    features,
    audienceRoles,
    audienceDesignations,
    audienceUserIds,
  };

  return (
    <>
      {error && (
        <div className="release-publish-error" role="alert">
          {error}
        </div>
      )}
      <ReleaseAnnouncementPopup
        release={previewRelease}
        mode="admin"
        publishing={publishing}
        audienceRoles={audienceRoles}
        audienceDesignations={audienceDesignations}
        audienceUserIds={audienceUserIds}
        onAudienceChange={setAudienceRoles}
        onAudienceDesignationsChange={setAudienceDesignations}
        onAudienceUserIdsChange={setAudienceUserIds}
        features={features}
        onFeaturesChange={setFeatures}
        onTitleChange={setTitle}
        onDismiss={handleLater}
        onPublish={handlePublish}
        onSkip={handleSkip}
      />
    </>
  );
};

export default ReleasePublishPrompt;
